import React, { useState } from 'react';
import { Calendar, Search, Filter, Clock, User, Video, Plus, Download, List, Table, Play } from 'lucide-react';
import './Appointments.css';

const Appointments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewType, setViewType] = useState('scheduled'); // 'scheduled' or 'past'
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentNotes, setAppointmentNotes] = useState({});

  // Mock scheduled appointments data
  const scheduledAppointments = [
    {
      id: 1,
      patientId: 42,
      patientName: 'Patient 42',
      patientAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format',
      date: 'Dec 8, 2025',
      time: '2:00 PM',
      dateTime: '2025-12-08T14:00:00',
      type: 'Follow-up Review',
      duration: '30 min',
      adherence: 85,
      status: 'confirmed',
      previousNotes: [
        {
          date: 'Nov 15, 2025',
          notes: 'Patient showing good adherence. Discussed medication schedule adjustments.'
        },
        {
          date: 'Oct 20, 2025',
          notes: 'Initial consultation. Set up HiDo device and reviewed medication protocol.'
        }
      ]
    },
    {
      id: 2,
      patientId: 44,
      patientName: 'Patient 44',
      patientAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format',
      date: 'Dec 5, 2025',
      time: '10:30 AM',
      dateTime: '2025-12-05T10:30:00',
      type: 'Adherence Check',
      duration: '20 min',
      adherence: 45,
      status: 'confirmed',
      previousNotes: [
        {
          date: 'Nov 10, 2025',
          notes: 'Patient has been missing doses. Discussed importance of adherence and set up reminder system.'
        }
      ]
    },
    {
      id: 3,
      patientId: 48,
      patientName: 'Patient 48',
      patientAvatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiM5Q0EzQUYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMzIgMzJDMzIgMjYuNDc3MiAyNy41MjI4IDIyIDIyIDIySDEyQzYuNDc3MTUgMjIgMiAyNi40NzcyIDIgMzJWNDBIMzJWMzJaIiBmaWxsPSIjRjlGQUZCIi8+Cjwvc3ZnPgo=',
      date: 'Dec 6, 2025',
      time: '3:30 PM',
      dateTime: '2025-12-06T15:30:00',
      type: 'Medication Review',
      duration: '25 min',
      adherence: 38,
      status: 'pending',
      previousNotes: []
    },
    {
      id: 4,
      patientId: 45,
      patientName: 'Patient 45',
      patientAvatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=40&h=40&fit=crop&crop=face&auto=format',
      date: 'Dec 15, 2025',
      time: '11:00 AM',
      dateTime: '2025-12-15T11:00:00',
      type: 'Routine Check-in',
      duration: '15 min',
      adherence: 92,
      status: 'confirmed',
      previousNotes: [
        {
          date: 'Nov 20, 2025',
          notes: 'Excellent adherence. Patient is comfortable with the system.'
        }
      ]
    }
  ];

  // Mock past appointments data
  const pastAppointments = [
    {
      id: 101,
      patientId: 42,
      patientName: 'Patient 42',
      patientAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format',
      date: 'Nov 15, 2025',
      time: '2:00 PM',
      dateTime: '2025-11-15T14:00:00',
      type: 'Follow-up Review',
      duration: '30 min',
      adherence: 82,
      status: 'completed',
      notes: 'Patient showing good adherence. Discussed medication schedule adjustments. Patient reports feeling better with current regimen.',
      videoRecordings: [
        {
          id: 1,
          title: 'Appointment Recording',
          duration: '28:45',
          thumbnail: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=240&fit=crop&auto=format'
        }
      ]
    },
    {
      id: 102,
      patientId: 44,
      patientName: 'Patient 44',
      patientAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format',
      date: 'Nov 10, 2025',
      time: '10:30 AM',
      dateTime: '2025-11-10T10:30:00',
      type: 'Adherence Check',
      duration: '20 min',
      adherence: 40,
      status: 'completed',
      notes: 'Patient has been missing doses. Discussed importance of adherence and set up reminder system. Family member present for support.',
      videoRecordings: [
        {
          id: 2,
          title: 'Appointment Recording',
          duration: '19:32',
          thumbnail: 'https://images.unsplash.com/photo-1584362917165-526a968579e8?w=400&h=240&fit=crop&auto=format'
        }
      ]
    },
    {
      id: 103,
      patientId: 63,
      patientName: 'Patient 63',
      patientAvatar: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=40&h=40&fit=crop&crop=face&auto=format',
      date: 'Nov 8, 2025',
      time: '1:00 PM',
      dateTime: '2025-11-08T13:00:00',
      type: 'Initial Consultation',
      duration: '45 min',
      adherence: null,
      status: 'completed',
      notes: 'Initial consultation completed. Set up HiDo device and reviewed all medications. Patient understands protocol.',
      videoRecordings: [
        {
          id: 3,
          title: 'Appointment Recording',
          duration: '43:12',
          thumbnail: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=400&h=240&fit=crop&auto=format'
        }
      ]
    }
  ];

  const appointments = viewType === 'scheduled' ? scheduledAppointments : pastAppointments;

  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = 
      apt.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleNoteChange = (appointmentId, note) => {
    setAppointmentNotes(prev => ({
      ...prev,
      [appointmentId]: note
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { text: 'Confirmed', class: 'status-confirmed' },
      pending: { text: 'Pending', class: 'status-pending' },
      completed: { text: 'Completed', class: 'status-completed' },
      cancelled: { text: 'Cancelled', class: 'status-cancelled' }
    };
    return statusConfig[status] || statusConfig.confirmed;
  };

  return (
    <div className="appointments-page">
      <div className="appointments-header">
        <div className="header-content">
          <h1>Appointments</h1>
          <p>Manage patient appointments and follow-up meetings</p>
        </div>
        <div className="header-actions">
          <button className="action-button primary">
            <Plus size={16} />
            New Appointment
          </button>
          <button className="action-button secondary">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="appointments-controls">
        <div className="search-filter">
          <Search className="search-icon" size={16} />
          <input
            type="text"
            placeholder="Search by patient or appointment type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-search"
          />
        </div>
        
        <div className="view-toggle">
          <button 
            className={`view-toggle-btn ${viewType === 'scheduled' ? 'active' : ''}`}
            onClick={() => setViewType('scheduled')}
          >
            <Calendar size={16} />
            Scheduled
          </button>
          <button 
            className={`view-toggle-btn ${viewType === 'past' ? 'active' : ''}`}
            onClick={() => setViewType('past')}
          >
            <List size={16} />
            Past
          </button>
        </div>
      </div>

      {viewType === 'scheduled' && (
        <div className="appointments-layout">
          <div className="appointments-list">
            {filteredAppointments.map((apt) => {
              const statusBadge = getStatusBadge(apt.status);
              return (
                <div 
                  key={apt.id} 
                  className={`appointment-card ${selectedAppointment?.id === apt.id ? 'selected' : ''}`}
                  onClick={() => setSelectedAppointment(apt)}
                >
                  <div className="appointment-header">
                    <div className="patient-info">
                      <img src={apt.patientAvatar} alt={apt.patientName} className="patient-avatar" />
                      <div>
                        <h3>{apt.patientName}</h3>
                        <span className="appointment-type">{apt.type}</span>
                      </div>
                    </div>
                    <div className={`status-badge ${statusBadge.class}`}>
                      {statusBadge.text}
                    </div>
                  </div>
                  <div className="appointment-details">
                    <div className="detail-item">
                      <Calendar size={14} />
                      <span>{apt.date}</span>
                    </div>
                    <div className="detail-item">
                      <Clock size={14} />
                      <span>{apt.time} ({apt.duration})</span>
                    </div>
                    <div className="detail-item">
                      <User size={14} />
                      <span>Adherence: {apt.adherence}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="appointment-detail">
            {selectedAppointment ? (
              <>
                <div className="detail-header">
                  <div className="detail-patient-info">
                    <img src={selectedAppointment.patientAvatar} alt={selectedAppointment.patientName} className="patient-avatar-large" />
                    <div>
                      <h2>{selectedAppointment.patientName}</h2>
                      <p>{selectedAppointment.type}</p>
                    </div>
                  </div>
                  <div className={`status-badge-large ${getStatusBadge(selectedAppointment.status).class}`}>
                    {getStatusBadge(selectedAppointment.status).text}
                  </div>
                </div>

                <div className="detail-info-grid">
                  <div className="info-item">
                    <label>Date & Time</label>
                    <p>{selectedAppointment.date} at {selectedAppointment.time}</p>
                  </div>
                  <div className="info-item">
                    <label>Duration</label>
                    <p>{selectedAppointment.duration}</p>
                  </div>
                  <div className="info-item">
                    <label>Patient Adherence</label>
                    <p>{selectedAppointment.adherence}%</p>
                  </div>
                </div>

                <div className="notes-section">
                  <h3>Appointment Notes</h3>
                  <textarea
                    className="notes-textarea"
                    placeholder="Enter notes for this appointment..."
                    value={appointmentNotes[selectedAppointment.id] || ''}
                    onChange={(e) => handleNoteChange(selectedAppointment.id, e.target.value)}
                    rows={6}
                  />
                  <button className="save-notes-btn">Save Notes</button>
                </div>

                {selectedAppointment.previousNotes && selectedAppointment.previousNotes.length > 0 && (
                  <div className="previous-notes">
                    <h3>Previous Follow-up Notes</h3>
                    {selectedAppointment.previousNotes.map((note, idx) => (
                      <div key={idx} className="previous-note">
                        <div className="note-date">{note.date}</div>
                        <p>{note.notes}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="no-selection">
                <Calendar size={48} />
                <h3>Select an appointment</h3>
                <p>Choose an appointment from the list to view details and add notes</p>
              </div>
            )}
          </div>
        </div>
      )}

      {viewType === 'past' && (
        <div className="past-appointments-list">
          {filteredAppointments.map((apt) => (
            <div key={apt.id} className="past-appointment-card">
              <div className="past-appointment-header">
                <div className="patient-info">
                  <img src={apt.patientAvatar} alt={apt.patientName} className="patient-avatar" />
                  <div>
                    <h3>{apt.patientName}</h3>
                    <span className="appointment-type">{apt.type}</span>
                  </div>
                </div>
                <div className="appointment-meta">
                  <div className="detail-item">
                    <Calendar size={14} />
                    <span>{apt.date} at {apt.time}</span>
                  </div>
                  <div className="detail-item">
                    <Clock size={14} />
                    <span>{apt.duration}</span>
                  </div>
                </div>
              </div>

              <div className="past-appointment-content">
                <div className="notes-display">
                  <h4>Appointment Notes</h4>
                  <p>{apt.notes}</p>
                </div>

                {apt.videoRecordings && apt.videoRecordings.length > 0 && (
                  <div className="video-recordings">
                    <h4>Video Recordings</h4>
                    <div className="video-recordings-grid">
                      {apt.videoRecordings.map((video) => (
                        <div key={video.id} className="video-recording-card">
                          <div className="video-thumbnail">
                            <img src={video.thumbnail} alt={video.title} />
                            <div className="video-overlay">
                              <Play size={32} fill="white" />
                            </div>
                            <div className="video-duration">{video.duration}</div>
                          </div>
                          <p className="video-title">{video.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredAppointments.length === 0 && (
        <div className="no-results">
          <Calendar size={48} />
          <h3>No appointments found</h3>
          <p>Try adjusting your search terms or create a new appointment</p>
        </div>
      )}
    </div>
  );
};

export default Appointments;

